/**
 * Trigger-rule display helpers — shared across the three surfaces that
 * render a row per rule:
 *   1. TriggerRulesForRepo (workspace-settings, repo-centric)
 *   2. TriggersSection (template-centric, inside ReplayTemplateDialog)
 *   3. Variant Automation page (variant-centric)
 *
 * Post-#1174 (meta #1172), every TriggerRule is a `deployment_status`
 * rule — the `push` / `pull_request` event types retired with the
 * backend rewrite in Slice 1 (#1173) and the editor + list rewrite in
 * Slice 2 (#1174). The helpers below collapsed accordingly: one pill
 * vocabulary (`Deploy`), one row-body shape (the optional environment
 * filter is the only variation). The seam stays per-rule rather than
 * inlined because all three surfaces still call into these helpers,
 * and a future second event type (a `release` event, a `discussion`
 * event) would land as additive branches here without each surface
 * relearning the dispatch.
 */
import type { TriggerRule } from '@/types'

/** Returns a short human-readable label for the rule's event type.
 * Every rule today is a `deployment_status` rule, so the label is a
 * constant; the function shape is kept to make a future second event
 * type an additive branch rather than a per-surface refactor. */
export function eventPillLabel(_rule: TriggerRule): string {
  return 'Deploy'
}

/** Tailwind class string for the event pill. The Deploy pill uses
 * purple tones, mirroring the mockup's `.ev.deploy` styling
 * (`docs/mockups/deployment-status-trigger.html`, Step 02). */
export function eventPillClass(_rule: TriggerRule): string {
  return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
}

/** Plain-text one-line description of a rule's event side. The caller
 * is responsible for rendering the environment in italics if it wants
 * the mockup's `<em>Preview</em>` styling — the helper returns prose
 * so it stays trivially testable and reusable across surfaces.
 *
 * Examples:
 *   On successful deployments to Preview
 *   On every successful deployment
 *
 * Customer-facing copy rule (CLAUDE.md): the environment is rendered
 * verbatim from whatever the deploy platform reported (`Preview`,
 * `Production`, customer-set names) — we never translate it. */
export function ruleEventDescription(rule: TriggerRule): string {
  const env = ruleEnvironmentFilter(rule)
  if (env) {
    return `On successful deployments to ${env}`
  }
  return 'On every successful deployment'
}

/** Render fragments for the rule's event line, with the environment
 * filter separated so callers can wrap it in `<em>` (the mockup's
 * styling). Returns a tuple of `[prefix, environment, suffix]` where
 * `environment` is `null` for any-environment rules.
 *
 * Example for a rule with `environment: 'Preview'`:
 *   ['On successful deployments to ', 'Preview', '']
 *
 * Example for a rule with no environment filter:
 *   ['On every successful deployment', null, '']
 */
export function ruleEventFragments(rule: TriggerRule): [string, string | null, string] {
  const env = ruleEnvironmentFilter(rule)
  if (env) {
    return ['On successful deployments to ', env, '']
  }
  return ['On every successful deployment', null, '']
}

/** Trim + normalize the rule's environment filter. Returns `null` for
 * empty / whitespace-only values so call sites have one falsy check
 * rather than two. */
function ruleEnvironmentFilter(rule: TriggerRule): string | null {
  const raw = rule.environment
  if (!raw) return null
  const trimmed = raw.trim()
  return trimmed === '' ? null : trimmed
}
